import React, { useEffect, useState, useRef, useContext } from 'react';
import { Space, Table, Button, Input, Modal, Select, message } from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import CreateModalContent from './CreateModalContent';
import IconGroup from './IconGroup';
import { LoadingIcon } from '../../../components';
import {
  getOrgs,
  getSites,
  getUsers,
  addUser,
  putUser,
  putUserStatus,
  deleteUser,
  resendInvite,
} from '../../../utils/api';
import { useLocalStorage } from '../../../hooks';
import { UserContext } from '../../../context/UserContext';

const SiteTable = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  currentSite,
  setCurrentSite,
  siteData,
}) => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [siteLoading, setSiteLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // const [currentOrg, setCurrentOrg] = useState(null);
  // const [currentSite, setCurrentSite] = useState(null);
  // const [orgData, setOrgData] = useState([]);
  // const [siteData, setSiteData] = useState([]);
  const searchInput = useRef(null);
  const { userData } = useContext(UserContext);

  const showCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCreateCancel = () => {
    setCreateModalOpen(false);
  };

  const renderBoolean = (bool) => (
    <p className={`${bool ? 'text-primaryBlue' : 'text-dangerRed'}`}>
      {String(bool)}
    </p>
  );

  // START - API Functions

  const fetchUsers = async (orgId, siteId = null) => {
    try {
      setDataLoading(true);
      const res = await getUsers(orgId, siteId);

      const data = res.data?.data.map((user) => {
        return {
          ...user,
          key: user.id,
        };
      });
      console.log({ data });
      setData(data);
    } catch (err) {
      message.error('Error fetching Users!');
    } finally {
      setDataLoading(false);
    }
  };

  const createUser = async (newData) => {
    try {
      const reqBody = { ...newData, organization_id: currentOrg };
      const res = await addUser(reqBody);
      const res2 = await fetchUsers(currentOrg, currentSite);
      console.log({ res });
    } catch (err) {
      message.error('Error creating User!');
    }
  };

  const updateUser = async (id, data) => {
    try {
      const res = await putUser(id, data);
      const res2 = await fetchUsers(currentOrg, currentSite);
      console.log({ res });
    } catch (err) {
      message.error('Error updating User!');
    }
  };

  const hideUser = async (id, data) => {
    try {
      const res = await putUserStatus(id, data);
      const res2 = await fetchUsers(currentOrg, currentSite);
      console.log({ res });
    } catch (err) {
      message.error('Error hiding User!');
    }
  };

  const removeUser = async (id) => {
    try {
      const res = await deleteUser(id);
      const res2 = await fetchUsers(currentOrg, currentSite);
      console.log({ res });
    } catch (err) {
      message.error('Error deleting User!');
    }
  };

  const inviteUser = async (id) => {
    try {
      const res = await resendInvite(id);
      console.log({ res });
    } catch (err) {
      message.error('Error sending Invite!');
    }
  };
  // END - API Functions

  // Fetch orgs on component mount
  // useEffect(() => {
  //   if (userData) {
  //     if (userData.is_superuser) {
  //       fetchOrgs();
  //     } else {
  //       console.log(userData?.organization_id);
  //       setCurrentOrg(userData?.organization_id);
  //       setSiteData(
  //         userData?.site_admins.map((site) => ({ ...site, key: site.id }))
  //       );
  //     }
  //   }
  // }, [userData]);

  useEffect(() => {
    const fn = async () => {
      if (currentOrg) {
        await fetchUsers(currentOrg, currentSite);
      }
    };
    fn();
  }, [currentOrg, currentSite]);

  // Fetch Site on org Change
  // useEffect(() => {
  //   const fn = async () => {
  //     if (currentOrg && (userData?.is_superuser || userData?.isAdmin)) {
  //       await fetchSites(currentOrg);
  //     }
  //   };
  //   fn();
  // }, [currentOrg]);

  // Update currentSite on SiteData change
  // useEffect(() => {
  //   setCurrentSite(siteData[0]?.key);
  // }, [siteData]);

  const columns = [
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'fname',
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'lname',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'active',
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className='text-successGreen' />
        ) : (
          <CloseCircleOutlined className='text-dangerRed' />
        ),
    },
    {
      title: 'Admin',
      dataIndex: 'is_admin',
      key: 'admin',
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className='text-successGreen' />
        ) : (
          <CloseCircleOutlined className='text-dangerRed' />
        ),
    },
    {
      title: 'Superuser',
      dataIndex: 'is_superuser',
      key: 'superuser',
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className='text-successGreen' />
        ) : (
          <CloseCircleOutlined className='text-dangerRed' />
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, record) => (
        <IconGroup
          ele={record}
          updateCallback={updateUser}
          hideCallback={hideUser}
          deleteCallback={removeUser}
          resendCallback={inviteUser}
          siteData={siteData}
        />
      ),
    },
  ];

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size='small'
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({
                closeDropdown: true,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#39f' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const finalColumns = columns.map((vals) => {
    const obj =
      vals.key === 'actions'
        ? { ...vals }
        : { ...vals, ...getColumnSearchProps(vals.key) };
    return obj;
  });

  return (
    <div>
      {dataLoading ? (
        <LoadingIcon classname='absolute h-full top-[40vh]' height="200px" />
      ) : (
        <>
          <div
            className={`w-full pb-10 h-fit flex justify-between items-center`}
          >
            <div className='flex'>
              {userData?.is_superuser ? (
                <Select
                  className={`min-w-[150px] mr-5`}
                  placeholder='Select Organization'
                  value={currentOrg}
                  onChange={setCurrentOrg}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={orgData.map((org) => ({
                    value: org.key,
                    label: org.name,
                  }))}
                />
              ) : null}
              {!siteLoading ? (
                <Select
                  className='min-w-[150px] mr-5'
                  placeholder='Select Site'
                  value={currentSite}
                  onChange={setCurrentSite}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label || "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={siteData.map((site) => ({
                    value: site.id,
                    label: site.display_name,
                  }))}
                />
              ) : (
                <div className='w-[150px]'>
                  <LoadingIcon height='30px' />
                </div>
              )}
            </div>
            <Button type='primary' onClick={() => setCreateModalOpen(true)}>
              Create User
            </Button>
          </div>

          <Table
            dataSource={data}
            columns={finalColumns}
            pagination={{
              hideOnSinglePage: true,
              position: ['bottomCenter'],
            }}
          />
        </>
      )}
      <Modal
        maskClosable={false}
        title={
          <div className='mb-5 font-IBMPlexSansBold text-2xl'>Create User</div>
        }
        open={createModalOpen}
        onCancel={handleCreateCancel}
        footer={null}
      >
        <CreateModalContent
          siteData={siteData}
          setOpen={setCreateModalOpen}
          createCallback={createUser}
        />
      </Modal>
    </div>
  );
};

export default SiteTable;
